import { useMemo, useState } from "react";
import { Link } from "react-router";
import MainLayout from "@/components/layouts";
import SimpleTable, { ColumnDef } from "@/components/shared/simple-table";
import { FilterItem } from "@/interfaces";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useIndividuals } from "./individuals.hooks";
import { IndividualWithJoin } from "./individuals.entity";
import { toast } from "sonner";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar.tsx";
import { getInitials } from "@/lib/utils.ts";
import { organization_user_status } from "@/models/organization-user.model";
import { InviteIndividualModal } from "./components/invite-individual-modal";
import StatusBadge from "@/app/organization/settings/teammembers/teammembers.components/StatusBadge";
import { toUserStatus } from "@/repositories/organization-records";
import { MoreHorizontal } from "lucide-react";
import {
  DropdownMenuContent,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import EditIndividualStatusModal from "./components/edit-individual-status-modal";
import type { WorkerContractSortKey } from "@/repositories/individuals.repository";

const sortKeyOf = (key: string): WorkerContractSortKey => (key === "email" ? "email" : "name");

export default function IndividualsPage() {
  const {
    individuals,
    total,
    page,
    pageSize,
    name,
    service,
    status,
    setPage,
    setPageSize,
    setName,
    setService,
    setStatus,
    sortKey,
    sortOrder,
    setSortKey,
    removeIndividual,
    openInviteModal,
    inviteOpen,
    inviteSaving,
    inviteError,
    sendInvite,
    editingModalOpen,
    editingIndividual,
    handleEditIndividual,
    saveIndividualStatus,
    setEditingModalOpen,
  } = useIndividuals();

  const [removeId, setRemoveId] = useState<string | null>(null);

  const serviceOptions = useMemo(() => {
    const names = Array.from(new Set(individuals.flatMap((e) => e.services.map((s) => s.name))));
    return names.map((n) => ({ label: n, value: n }));
  }, [individuals]);

  const columns: ColumnDef<IndividualWithJoin>[] = [
    {
      header: "Name",
      accessor: (row) => (
        <Link to={`/individuals/${row.id}`} className="hover:underline flex items-center">
          <Avatar className="w-8 h-8 mr-4">
            <AvatarImage src={row.person.avatar ?? undefined} />
            <AvatarFallback>{getInitials(row.person.name)}</AvatarFallback>
          </Avatar>
          {row.person.name}
        </Link>
      ),
      sortable: true,
      sortKey: "name",
    },
    {
      header: "Email",
      accessor: (row) => row.person.email,
      sortable: true,
      sortKey: "email",
    },
    {
      header: "Services",
      accessor: (row) => row.services.map((s) => s.name).join(", ") || "—",
    },
    {
      header: "Status",
      accessor: (row) => <StatusBadge status={toUserStatus(row.status)} />,
      className: "whitespace-nowrap",
    },
    {
      header: "Actions",
      accessor: (row) => (
        <div className="flex gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="icon" variant="ghost">
                <MoreHorizontal className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <Link to={`/individuals/${row.id}`}>
                <DropdownMenuItem>View Details</DropdownMenuItem>
              </Link>
              <DropdownMenuItem
                onSelect={() => {
                  setTimeout(() => handleEditIndividual(row), 0);
                }}
              >
                Edit Status
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={() => {
                  setTimeout(() => setRemoveId(row.id), 0);
                }}
                className="text-destructive"
              >
                Remove Individual
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <Dialog open={removeId === row.id} onOpenChange={(open) => setRemoveId(open ? row.id : null)}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Remove Individual?</DialogTitle>
              </DialogHeader>
              <p className="pt-2 text-sm text-muted-foreground">
                Are you sure you want to remove <strong>{row.person.name}</strong>? You can re-invite them later if
                needed.
              </p>
              <DialogFooter className="pt-4">
                <Button variant="outline" onClick={() => setRemoveId(null)}>
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={async () => {
                    setRemoveId(null);
                    await removeIndividual(row.id);
                    toast.success("Individual removed successfully.");
                  }}
                >
                  Remove
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      ),
      className: "whitespace-nowrap",
    },
  ];

  const statusOptions = [
    { label: "Invited", value: organization_user_status.invited },
    { label: "Pending", value: organization_user_status.pending },
    { label: "Active", value: organization_user_status.active },
    { label: "Suspended", value: organization_user_status.suspended },
  ];

  const filterItems: FilterItem[] = [
    {
      type: "search",
      title: "Name",
      value: name,
      onValueChange: setName,
    },
    {
      type: "select",
      title: "Status",
      value: status,
      onValueChange: setStatus,
      options: statusOptions,
    },
    {
      type: "select",
      title: "Service",
      value: service,
      onValueChange: setService,
      options: serviceOptions,
    },
  ];

  return (
    <MainLayout
      breadcrumb={[{ name: "Individuals", href: "#" }]}
      pageHeader={{
        title: "Individuals",
        subtitle: "Manage your team individuals and their assignments",
        buttonText: "+ Invite New Individual",
        onButtonClick: () => openInviteModal(true),
      }}
    >
      <SimpleTable<IndividualWithJoin>
        columns={columns}
        data={individuals}
        totalCount={total}
        page={page}
        pageSize={pageSize}
        setPage={setPage}
        setPageSize={setPageSize}
        sortKey={sortKey}
        sortOrder={sortOrder}
        onSortChange={(key) => setSortKey(sortKeyOf(key))}
        filters={filterItems}
        onReset={() => {
          setName("");
          setStatus("");
          setService("");
        }}
      />

      <InviteIndividualModal
        open={inviteOpen}
        saving={inviteSaving}
        error={inviteError}
        onOpenChange={openInviteModal}
        onSubmit={sendInvite}
      />

      <EditIndividualStatusModal
        open={editingModalOpen}
        onOpenChange={setEditingModalOpen}
        currentStatus={editingIndividual?.status ?? organization_user_status.active}
        onSubmit={saveIndividualStatus}
      />
    </MainLayout>
  );
}
